import Image from 'next/image'
import { FC, useEffect, useState } from 'react'

import { useFilePreview } from '@/api/models/requests/hooks/useFilePreview'
import { FileType } from '@/api/models/requests/lib/requests.types'
import {
  fetchRemoteFileSize,
  formatFileSize
} from '@/api/models/requests/lib/requests.utils'
import { FileApiType } from '@/api/shared/types'
import { FileCard } from '@/components/features/upload-files'
import { PreviewImagesSlider } from '@/components/features/upload-files/ui/PreviewImagesSlider'
import { PreviewModal } from '@/components/shared/modal/ui/PreviewModal'

import { AnimatedLoader } from '../../loader/ui/AnimatedLoader'

type Props = {
  files: FileApiType[]
  className?: {
    rootClassName?: string
    fileNameClassName?: string
  }
}

const FileUploadedList: FC<Props> = ({ files, className }) => {
  const {
    isModalOpen,
    currentFiles,
    activeIndex,
    handleFileAction,
    handleCloseModal
  } = useFilePreview(files, FileType.Remote)

  const [fileSizes, setFileSizes] = useState<string[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const fetchAllSizes = async () => {
      setFileSizes([])

      const sizePromises = files.map(file => fetchRemoteFileSize(file.link))
      const sizes = await Promise.all(sizePromises)

      const formattedSizes = sizes.map(size =>
        size ? formatFileSize(size) : ''
      )

      setFileSizes(formattedSizes)
    }

    fetchAllSizes()
  }, [files])

  return (
    <div className="mb-24 flex flex-col gap-16 empty:mb-0">
      {files.map((file, index) => (
        <FileCard
          key={file.name + index}
          fileName={file.name}
          image={file.link}
          fileSizeWithUnit={fileSizes[index]}
          withoutDeleting
          handleFileAction={handleFileAction}
          className={className}
        />
      ))}
      <PreviewModal
        opened={isModalOpen}
        onClose={handleCloseModal}
        className="h-full w-full"
        contentClassName="flex justify-center items-center"
      >
        {!isLoaded && <AnimatedLoader dataTestId="file-preview-loader-id" />}
        {currentFiles.length === 1 && (
          <Image
            src={currentFiles[0].link}
            alt={currentFiles[0].name}
            fill
            data-testid="file-preview-image-id"
            className="!static max-h-[90vh] w-full object-contain"
            onLoad={() => setIsLoaded(true)}
          />
        )}
        {currentFiles.length > 1 && (
          <PreviewImagesSlider
            files={currentFiles}
            onClose={handleCloseModal}
            initialSlide={activeIndex}
          />
        )}
      </PreviewModal>
    </div>
  )
}

export default FileUploadedList
